/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { ConcurrentsState } from 'store/modules/concurrents_store/types';
import {
  getCompanyBiddingsActivities,
  participants,
  PARTICIPANTS_PER_PAGE,
} from 'store/modules/concurrents_store/actions';
import FollowupListConcurrents from 'components/atoms/followup_list_concurrents';
import Loader from 'components/atoms/loader';
import { Modalities } from 'services/modalities';
import Pagination from 'components/atoms/pagination';
import BiddingModal from 'components/atoms/bidding_modal';

interface ParticipationAnalysisProps {
  cnpj: string;
}

type TabKey = 'vencidos' | 'nao_vencidos';

const ParticipationAnalysis = ({ cnpj }: ParticipationAnalysisProps) => {
  const [currentPageFollowup, setCurrentPageFollowup] = useState<number>(1);
  const [activeTab, setActiveTab] = useState<TabKey>('vencidos');
  const [pageVencidos, setPageVencidos] = useState<number>(1);
  const [pageNaoVencidos, setPageNaoVencidos] = useState<number>(1);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedBidding, setSelectedBidding] = useState<number | null>(null);

  const { concurrents, request } = useSelector<ApplicationState, ConcurrentsState>(
    (state) => state.session.concurrents,
  );
  const dispatch = useDispatch();

  useEffect(() => {
    if (cnpj !== '') {
      dispatch(getCompanyBiddingsActivities(cnpj, currentPageFollowup));
    }
  }, [cnpj, currentPageFollowup, dispatch]);

  useEffect(() => {
    if (cnpj !== '') {
      dispatch(participants(cnpj, pageVencidos, pageNaoVencidos));
    }
  }, [cnpj, pageVencidos, pageNaoVencidos, dispatch]);

  const resumo = concurrents?.participacoes_resumo;
  const total = resumo?.participacoes || 0;
  const vencidas = resumo?.disputas_vencidas?.quantidade || 0;
  const valorVencido = resumo?.disputas_vencidas?.valor_total || 0;
  const naoVencidas = resumo?.disputas_nao_vencidas?.quantidade || 0;
  const percVencidas = total > 0 ? (vencidas / total) * 100 : 0;
  const percNaoVencidas = total > 0 ? (naoVencidas / total) * 100 : 0;
  const paginacao = concurrents?.participacoes_paginacao;
  const perPage = paginacao?.per_page || PARTICIPANTS_PER_PAGE;
  const carregando = request?.participacoes_resumo?.loading;

  const formatarValor = (valor?: number | string | null) => {
    if (valor === null || valor === undefined || valor === '') return 'R$ 0,00';

    let numero: number;
    if (typeof valor === 'string') {
      const limpo = valor.replace(/[^\d,.-]/g, '');
      numero = Number(limpo.includes('.') ? limpo.replace(/,/g, '') : limpo.replace(',', '.'));
    } else {
      numero = valor;
    }

    if (Number.isNaN(numero)) return 'R$ 0,00';

    return numero.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const openBidding = (id?: number) => {
    if (!id) return;
    setSelectedBidding(id);
    setShowModal(true);
  };

  const listas = [
    {
      key: 'vencidos' as TabKey,
      titulo: 'Itens vencidos',
      itens: resumo?.disputas_vencidas?.itens || [],
      valorTotal: resumo?.disputas_vencidas?.valor_total || 0,
      totalItens: paginacao?.vencidos?.total_itens ?? vencidas,
      page: pageVencidos,
      setPage: setPageVencidos,
      valorClass: 'text-success',
      vazio: 'Nenhum item vencido encontrado',
    },
    {
      key: 'nao_vencidos' as TabKey,
      titulo: 'Itens não vencidos',
      itens: resumo?.disputas_nao_vencidas?.itens || [],
      valorTotal: resumo?.disputas_nao_vencidas?.valor_total || 0,
      totalItens: paginacao?.nao_vencidos?.total_itens ?? naoVencidas,
      page: pageNaoVencidos,
      setPage: setPageNaoVencidos,
      valorClass: 'text-dark',
      vazio: 'Nenhum item não vencido encontrado',
    },
  ];

  return (
    <div>
      <div className="row">
        <div className="col-sm-3">
          <div className="card bg-light h-100">
            <div className="card-body">
              <span className="font-weight-light">Participações em disputas:</span>
              <h5 className="card-title font-weight-bold mt-2 mb-2">{total}</h5>
            </div>
          </div>
        </div>
        <div className="col-sm-3">
          <div className="card bg-light h-100">
            <div className="card-body">
              <span className="font-weight-light">Disputas não vencidas:</span>
              <h5 className="card-title font-weight-bold mt-2 mb-2">{naoVencidas}</h5>
            </div>
          </div>
        </div>
        <div className="col-sm-3">
          <div className="card bg-light h-100">
            <div className="card-body">
              <span className="font-weight-light">Disputas vencidas:</span>
              <h5 className="card-title font-weight-bold mt-2 mb-2">{vencidas}</h5>
            </div>
          </div>
        </div>
        <div className="col-sm-3">
          <div className="card bg-light h-100">
            <div className="card-body">
              <p className="card-text font-weight-light my-1">Valor total das disputas vencidas:</p>
              <h5 className="card-title font-weight-bold text-success">
                {formatarValor(Number(valorVencido))}
              </h5>
            </div>
          </div>
        </div>
      </div>
      <div className="row mt-3">
        <div className="col-md-12">
          <div className="card card-progress p-3 mb-3 bg-light">
            <h6 className="card-title font-weight-bold text-dark">
              Vitórias vs derrotas em relação ao total participado:
            </h6>
            <p className="text-primary mb-1">● Disputas vencidas {percVencidas.toFixed(1)}%</p>
            <p className="text-danger mb-2">
              ● Disputas não vencidas {percNaoVencidas.toFixed(1)}%
            </p>
            <div className="progress">
              <div
                className="progress-bar bg-primary"
                style={{ width: `${percVencidas}%` }}
                aria-valuenow={Number(percVencidas.toFixed(1))}
                aria-valuemin={0}
                aria-valuemax={100}
              />
              <div
                className="progress-bar bg-danger"
                style={{ width: `${percNaoVencidas}%` }}
                aria-valuenow={Number(percNaoVencidas.toFixed(1))}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-6">
          <div className="card bg-light h-100">
            <div className="card-body">
              <span className="font-weight-bold text-dark">Contratos vencidos por estado:</span>
              <ul className="list-group list-group-flush">
                {resumo?.licitacoes_por_estado?.length ? (
                  resumo.licitacoes_por_estado.map((item) => (
                    <li
                      key={item.estado}
                      className="list-group-item bg-light d-flex justify-content-between align-items-center pl-0"
                    >
                      <span>{item.estado}</span>
                      <span className="d-flex align-items-center">
                        <span className="text-success font-weight-bold mx-1">
                          {formatarValor(item.valor_total)}
                        </span>
                        <span className="text-muted">| {item.participacoes} participações</span>
                      </span>
                    </li>
                  ))
                ) : (
                  <li className="list-group-item bg-light pl-0 text-muted">
                    Nenhum dado disponível
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card bg-light h-100">
            <div className="card-body">
              <span className="font-weight-bold text-dark">Licitações por modalidade:</span>
              <ul className="list-group list-group-flush">
                {resumo?.licitacoes_por_modalidade?.length ? (
                  resumo.licitacoes_por_modalidade.map((item) => {
                    const modalidadeNome =
                      Modalities.find((m) => m.id === Number(item.modalidade))?.nome ||
                      `Modalidade ${item.modalidade}`;

                    return (
                      <li
                        key={item.modalidade}
                        className="list-group-item bg-light d-flex justify-content-between align-items-center pl-0"
                      >
                        <span>{modalidadeNome}</span>
                        <span className="d-flex align-items-center">
                          <span className="text-success font-weight-bold mx-1">
                            {formatarValor(item.valor_total)}
                          </span>
                          <span className="text-muted">| {item.participacoes} participações</span>
                        </span>
                      </li>
                    );
                  })
                ) : (
                  <li className="list-group-item bg-light pl-0 text-muted">
                    Nenhum dado disponível
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className="row mt-3">
        <div className="col-md-12">
          <div className="card bg-light p-3">
            <ul className="nav nav-tabs mb-3 d-print-none" role="tablist">
              {listas.map((lista) => (
                <li className="nav-item" key={lista.key}>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === lista.key}
                    className={`nav-link font-weight-bold ${
                      activeTab === lista.key ? 'active' : 'text-dark'
                    }`}
                    onClick={() => setActiveTab(lista.key)}
                  >
                    {lista.titulo}
                    <span className="badge badge-pill badge-secondary ml-2">
                      {lista.totalItens}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <div className="tab-content">
              {listas.map((lista) => (
                <div
                  key={lista.key}
                  role="tabpanel"
                  className={activeTab === lista.key ? 'd-block' : 'd-none d-print-block'}
                >
                  <h6 className="font-weight-bold text-dark d-none d-print-block">
                    {lista.titulo}:
                  </h6>
                  {carregando ? (
                    <Loader />
                  ) : lista.itens.length ? (
                    <div className="table-responsive">
                      <table className="table table-bordered table-striped mb-0">
                        <thead className="thead-dark">
                          <tr>
                            <th scope="col">N.º CNL</th>
                            <th scope="col">Item</th>
                            <th scope="col">Quantidade</th>
                            <th scope="col">Valor unitário</th>
                            <th scope="col">Valor total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lista.itens.map((item, index) => (
                            <tr key={index}>
                              <td>
                                {item?.bidding_id ? (
                                  <button
                                    type="button"
                                    className="btn btn-link p-0"
                                    style={{ textDecoration: 'underline' }}
                                    onClick={() => openBidding(Number(item.bidding_id))}
                                    title="Abrir detalhes do edital"
                                  >
                                    {item?.bidding_id}
                                  </button>
                                ) : (
                                  '-'
                                )}
                              </td>
                              <td>{item?.descricao || 'Sem descrição'}</td>
                              <td>{item?.quantidade ?? 0}</td>
                              <td className={`font-weight-bold ${lista.valorClass}`}>
                                {formatarValor(Number(item?.valor_unitario))}
                              </td>
                              <td className={`font-weight-bold ${lista.valorClass}`}>
                                {formatarValor(Number(item?.valor_final))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="row d-print-none">
                        <div className="col-md-12 px-4 mb-1 mt-2">
                          <Pagination
                            postsPerPage={perPage}
                            totalPosts={lista.totalItens}
                            paginate={(page) => lista.setPage(Math.max(1, page))}
                            currentPage={lista.page}
                            firstAndLastPage
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted mb-0">{lista.vazio}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="row my-3 px-3">
        <div className="card shadow col-12 p-0">
          <div className="card-title m-0 p-3 navbar mb-0">
            <h6 className="m-0 font-weight-bold">
              Licitações nos acompanhamentos em que este CNPJ é citado.
            </h6>
          </div>
          <div>
            {concurrents?.company?.length === 0 ? (
              <h6 className="text-center m-1 font-weight-bold my-3">Lista de licitações</h6>
            ) : request?.get_followups?.loading ? (
              <Loader />
            ) : (
              <FollowupListConcurrents
                setCurrentPageFollowup={setCurrentPageFollowup}
                currentPageFollowup={currentPageFollowup}
              />
            )}
          </div>
        </div>
      </div>
      <BiddingModal showModal={showModal} setShowModal={setShowModal} bidding={selectedBidding} />
    </div>
  );
};

export default ParticipationAnalysis;
